// Modules
import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

// Application
import { useAnalytics, useActiveCIPOrphanHook, useLoginHook, useRedirectExternalUniqueToken } from 'hooks'
import { CPFSchema } from 'validators/schema'

import { AccountExistsModalInstances } from 'constants/accountExistsModal'
import { Analytics } from 'constants/analytics'
import { HelpModalInstances } from 'constants/helpmodal'
import { convertToNumber, formattedBirthDateUSA, getTokenType, singleSPA } from 'utils'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'

// Components
import { CPF, CPFInitial } from 'components/Presenters/CPF'
import { Header, TemplateContainer } from 'components'
import { ROUTES_PATH } from 'constants/routesPath'

const INFOBOX_PARAGRAPH =
    'Esta informação permite que você seja identificado no sistema e garante a autenticidade das informações de forma segura.'
const INFOBOX_TITLE = 'POR QUE PRECISO DO SEU CPF?'
const INFOBOX_LINK_TEXT = 'Saber mais sobre dados coletados'
const INFOBOX_LINK_URL = 'https://ajuda.nav.com.br/hc/pt-br/articles/360062097192'

const LIVIA_LS_KEY = '@Livia'
export const ACOCPF: React.VFC = () => {
    const { verifyQueryParamsAndSaveSessionStorage } = useRedirectExternalUniqueToken()

    const [isLoginFap, setIsLoginFap] = useState(false)
    const {
        setSubmitting,
        viewerSearchLogin,
        viewerSearchPatient,
        openHelpModal,
        setPersonalData,
        isSubmitting,
        setTokenCIP,
        openAccountExistsModal,
    } = useActiveCIPOrphanHook()

    const { authState } = useLoginHook()

    const history = useHistory()

    useEffect(() => {
        const storage = localStorage.getItem(LIVIA_LS_KEY)

        if (storage) {
            const { token: tokenCIP } = JSON.parse(storage)
            setTokenCIP(tokenCIP)
            setIsLoginFap(getTokenType(tokenCIP) === 'FAP')
        }

        verifyQueryParamsAndSaveSessionStorage()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { refreshToken, limitedFlow } = authState
    const shouldRedirect = !!refreshToken && !limitedFlow

    useAnalytics(Analytics.ActivateAccountCipOrphan.Cpf, !shouldRedirect)

    if (shouldRedirect) {
        singleSPA.historyPush(defaultConfig.strings.AUTH_REDIRECT_LOGIN_LIMITED_FLOW)
        return null
    }

    const handleSubmit = async (values: CPFInitial) => {
        const { username } = values

        // eslint-disable-next-line prefer-destructuring
        const { name, birthDate, isInvalid } = authState.user || {}

        try {
            await setSubmitting(true)
            await CPFSchema.validate(values)
            if (isInvalid) {
                openHelpModal(isLoginFap ? HelpModalInstances.accountFapOrphanIsInvalid : HelpModalInstances.accountCipOrphanIsInvalid)
                await setSubmitting(false)
                return
            }

            const status = await viewerSearchLogin(username)
            if (status) {
                openAccountExistsModal(AccountExistsModalInstances.accountCipOrphanAccountExists)
                await setSubmitting(false)
                return
            }

            const { id, biologicalSex, cep, phoneContacts, emailContacts } = await viewerSearchPatient({
                name,
                cpf: username,
                birthDate,
            })

            const personalData = {
                id,
                name,
                cpf: convertToNumber(username),
                birthDate: formattedBirthDateUSA(birthDate),
                biologicalSex,
                postalCode: cep,
                phoneContacts,
                emailContacts,
            }

            await setPersonalData(personalData)
            await setSubmitting(false)
            history.push(ROUTES_PATH.SIGN_UP_MDM_CIP_CREATE_PASSWORD)
        } catch (err) {
            const personalData = {
                name,
                cpf: convertToNumber(username),
                birthDate: formattedBirthDateUSA(birthDate),
            }
            await setPersonalData(personalData)
            await setSubmitting(false)
            history.push(ROUTES_PATH.SIGN_UP_NEW_CIP_BIOLOGICAL_SEX)
        }
    }

    return (
        <TemplateContainer>
            <Header subtitle="Preencha o campo abaixo com os dados do seu CPF." title="Qual é o seu CPF?" />
            <CPF
                handleSubmit={handleSubmit}
                infoBox={{
                    paragraph: INFOBOX_PARAGRAPH,
                    title: INFOBOX_TITLE,
                    link: {
                        text: INFOBOX_LINK_TEXT,
                        to: INFOBOX_LINK_URL,
                    },
                }}
                initialValues={{ username: '' }}
                isOnlyCPF={{ validateSchema: CPFSchema }}
                isSubmitting={isSubmitting}
            />
        </TemplateContainer>
    )
}
